/**
 * ExtraData payload for ArchiPro `DownloadsClick` / `DownloadsLoggedOutClick`.
 *
 * Stored as JSON so Pixel session SQL can read `fileUrl` / `fileTitle` while
 * ConversionTableData still resolves `fileTitle` for Download conversion labels.
 * Plain-string ExtraData (title only) remains supported for historical rows.
 *
 * `MouseButton` is optional and only set by the attribute-driven tracker hook.
 */
export type DownloadsClickExtraData = {
    fileTitle: string;
    fileUrl: string;
    isExternal: boolean;
    MouseButton?: 0 | 1 | 2;
};

/** LoggedEvent.ExtraData is Varchar(255); keep JSON valid under that limit. */
export const DOWNLOADS_CLICK_EXTRA_DATA_MAX_LENGTH = 255;

/**
 * True when the destination is absolute http(s) or protocol-relative.
 * Shared by producers (isExternal) and BM labelling (External Link).
 */
export const isAbsoluteHttpUrl = (url: string): boolean => {
    const trimmed = url.trim();
    return trimmed.startsWith('//') || /^https?:\/\//i.test(trimmed);
};

type BuildDownloadsClickExtraDataInput = {
    fileTitle: string;
    fileUrl: string;
    MouseButton?: 0 | 1 | 2;
};

/**
 * Builds the canonical ExtraData JSON string for a download click.
 * Derives `isExternal` from `fileUrl` so all producers share one meaning.
 * Omits or shortens fields when needed so the payload fits Varchar(255).
 */
export const buildDownloadsClickExtraData = ({
    fileTitle,
    fileUrl,
    MouseButton,
}: BuildDownloadsClickExtraDataInput): string => {
    const isExternal = isAbsoluteHttpUrl(fileUrl);

    const serialise = (title: string, url: string): string => {
        const payload: DownloadsClickExtraData = {
            fileTitle: title,
            fileUrl: url,
            isExternal,
        };
        if (MouseButton !== undefined) {
            payload.MouseButton = MouseButton;
        }
        return JSON.stringify(payload);
    };

    let json = serialise(fileTitle, fileUrl);
    if (json.length <= DOWNLOADS_CLICK_EXTRA_DATA_MAX_LENGTH) {
        return json;
    }

    // Drop fileUrl rather than truncate mid-URL (invalid deep links).
    json = serialise(fileTitle, '');
    if (json.length <= DOWNLOADS_CLICK_EXTRA_DATA_MAX_LENGTH) {
        return json;
    }

    let title = fileTitle;
    while (title.length > 0) {
        title = title.slice(0, -1);
        json = serialise(title, '');
        if (json.length <= DOWNLOADS_CLICK_EXTRA_DATA_MAX_LENGTH) {
            return json;
        }
    }

    return json;
};
